"use client";

import type { AboutPage, Page } from '../../lib/types';
import { usePathname } from 'next/navigation';
import HeroAbout from '../ui/HeroAbout';
import PageBodyBackground from '../ui/PageBodyBackground';
import StaticClientsPartners from '../sections/StaticClientsPartners';
import Team from '../sections/Team';
import HeadingCentered from '../ui/HeadingCentered';
import Cookies from '../sections/Cookies';
import { trackEvent } from '@/lib/analytics';
import { useInView } from '@/lib/useInView';
import CountUpStatList from '../sections/CountUpStatList';
import LayoutAboutTitleCarousel from '../sections/LayoutAboutTitleCarousel';
import ScrollForMoreOverlay from '../ui/ScrollForMoreOverlay';

type AboutLayoutProps = {
  page: Page;
};

/**
 * StaticClientsPartnersContainer component with animate-in-view
 */
function StaticClientsPartnersContainer() {
  const { ref } = useInView<HTMLDivElement>();
  
  return (
    <div ref={ref} className="static-clients-partners-container animate-in-view">
      <StaticClientsPartners />
    </div>
  );
}


export default function AboutLayout({ page }: AboutLayoutProps) {
  const aboutPage = page as AboutPage;
  const pathname = usePathname();
  
  return (
    <>
      <PageBodyBackground heroId="hero-about" />
      
      {/* Hero Section */}
      <HeroAbout id="hero-about" />
      <ScrollForMoreOverlay />

      {/* Our global CX network */}
      <CountUpStatList
        stats={[
          { target: 2300, suffix: '+', delay: 0, label: 'people' },
          { target: 19, delay: 150, as: 'h3', label: 'countries' },
          { target: 26, delay: 300, as: 'h3', label: 'offices' }
        ]}
        ctaButton={{
          href: 'https://havascx.com/',
          text: 'About the Havas CX network',
          onClick: () => {
            trackEvent('who_we_are_click', {
              click_text: 'About the Havas CX network',
              click_url: 'https://havascx.com/',
              page_path: pathname
            });
          }
        }}
      />

      <div className="h-[4.125rem] lg:h-[7.5rem]"></div>
    
      {/* Some of our clients & partners */}
      <div className="relative mt-24 global-padding">
        <HeadingCentered title="Some of our clients & partners" />
        <StaticClientsPartnersContainer />
      </div>

      <div className="h-[5.4375rem] lg:h-[9.25rem]"></div>
      
      {/* US Leadership */}
      {aboutPage.teamMembers && aboutPage.teamMembers.length > 0 && (
        <Team variant="about" teamMembers={aboutPage.teamMembers} title="" body="U.S. Leadership" />
      )}

      <div className="h-[4.8125rem] lg:h-[9.875rem]"></div>

      {/* Join our team */}
      <LayoutAboutTitleCarousel />

      <div className="h-[1.125rem] lg:h-[0.5rem]"></div>

      {/* Cookies */}
      <Cookies />
    </>
  );
}
